import { CheckIcon, ExclamationTriangleIcon } from '@heroicons/react/16/solid'
import type { DraftState } from '../../../Model/OrderWizard/OrderWizardDraft.ts'
import { useDispatch } from '../../../Flux/index.ts'
import { orderWizardActions as wizard } from '../../../Flux/Slice/OrderWizard/OrderWizardSlice.ts'
import { archiveSamples, checkArchive, isArchiveOrder } from '../../../Services/OrderWizard/archive.ts'
import { GroupWriteFailure } from './GroupWriteFailure.tsx'
import { ItemTypeControl } from './ItemTypeControl.tsx'
import { groupTarget } from '../../../Services/OrderWizard/issues.ts'
import { plural } from '../../../Util/plural.ts'
import { SectionHeading, TagChip } from './Primitives.tsx'

const percent = (share: number) => `${Math.round(share * 100)}%`

const Tag = ({ value }: { value?: string }) =>
  value === undefined ? <span className="text-slate-400">?</span> : <TagChip mono>{value || '∅'}</TagChip>

/**
 * Per archive: the samples the sheet defines inside it (name, tags, sample or control, whether its item
 * exists), next to the tag pairs its reads carry. The read tags are an experimental estimate for checking
 * the sheet's completeness, never written.
 */
export const ArchivePanel = ({ draft }: { draft: DraftState }) => {
  const dispatch = useDispatch()
  if (!isArchiveOrder(draft)) {
    return null
  }
  const samplesByGroup = archiveSamples(draft)
  const groups = draft.fileOrder.map((key) => draft.groups[key]).filter((group) => !!group)
  if (groups.length === 0) {
    return null
  }
  return (
    <section className="space-y-3" data-testid="wizard-archives">
      <SectionHeading>Sample archives</SectionHeading>
      {groups.map((group) => {
        const samples = samplesByGroup[group.key] ?? []
        const check = checkArchive(group, samples)
        const census = group.archiveTags
        const inReads = new Set((census?.tags ?? []).map((tag) => `${tag.forwardTag || '∅'}+${tag.reverseTag || '∅'}`))
        return (
          <div key={group.key} id={groupTarget(group.key)} className="rounded-lg border border-slate-200 p-2 transition-shadow">
            <p className="font-mono text-xs text-slate-600">{draft.files[group.forward]?.name}</p>
            <GroupWriteFailure draft={draft} group={group} />
            <p className="mt-1 text-sm text-slate-700" data-testid="wizard-archive-summary">
              {samples.length === 0
                ? 'No samples defined yet — the sheet defines them.'
                : `The sheet defines ${plural(samples.length, 'sample')}.`}
              {census && census.located > 0 && (
                <>
                  {' '}
                  The reads show {plural(census.tags.length, 'frequent tag pair')}
                  {samples.length > 0 && `; the sheet's tags cover ${percent(check.coverage)} of the read pairs with primers`}
                  {check.undefinedInSheet.length > 0 &&
                    `; ${plural(check.undefinedInSheet.length, 'undefined tag pair')} carry ${percent(
                      check.undefinedInSheet.reduce((sum, tag) => sum + tag.share, 0)
                    )}`}
                  . <span className="text-xs text-slate-500">(estimate from {census.pairs} read pairs, shown only)</span>
                </>
              )}
            </p>
            {samples.length > 0 && (
              <div className="mt-1 max-h-[50vh] overflow-auto">
                <table className="table table-xs table-pin-rows">
                  <thead>
                    <tr>
                      <th>Sample</th>
                      <th>Tags (fwd + rev)</th>
                      <th>In reads</th>
                      <th>Type</th>
                      <th>Item</th>
                    </tr>
                  </thead>
                  <tbody>
                    {samples.map((sample) => {
                      const label =
                        sample.forwardTag !== undefined && sample.reverseTag !== undefined
                          ? `${sample.forwardTag || '∅'}+${sample.reverseTag || '∅'}`
                          : undefined
                      return (
                        <tr key={sample.key}>
                          <td>{sample.name}</td>
                          <td>
                            <Tag value={sample.forwardTag} /> + <Tag value={sample.reverseTag} />
                          </td>
                          <td>
                            {!census || !label ? (
                              <span className="text-slate-400">–</span>
                            ) : inReads.has(label) ? (
                              <CheckIcon className="h-3.5 w-3.5 text-success" />
                            ) : (
                              <span className="flex items-center gap-1 text-warning">
                                <ExclamationTriangleIcon className="h-3 w-3" /> not found
                              </span>
                            )}
                          </td>
                          <td>
                            <ItemTypeControl
                              name={sample.name}
                              control={sample.itemType === 'NEGATIVE_CONTROL'}
                              autoControl={sample.typeSource === 'auto'}
                              compact={false}
                              testId="wizard-archive-item-type"
                              onSetType={(itemType) =>
                                dispatch(wizard.archiveTypeToggled({ draftId: draft.id, groupKey: group.key, key: sample.key, itemType }))
                              }
                            />
                          </td>
                          <td className="text-slate-500">{group.archiveItems?.[sample.key] ? 'written' : '–'}</td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>
            )}
            {check.undefinedInSheet.length > 0 && (
              <p className="mt-1 text-xs text-amber-700" data-testid="wizard-archive-undefined">
                Read tags not in the sheet: {check.undefinedInSheet.map(({ tags, share }) => `${tags} (${percent(share)})`).join(', ')}
              </p>
            )}
          </div>
        )
      })}
    </section>
  )
}
